import {
  LayoutDashboard, // Dashboard
  LayoutGrid, // Service Catalog (or Library)
  Activity, // Service Usage (or Gauge)
  GitCompare, // Change Impact (or FileDiff)
  Megaphone, // What's New
  FileText, // Generate Report (Crucial fix)
  UserCog, // User Management
  LogOut,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import React from "react";
import { cn } from "../lib/cn";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "../components/ui5";

// Menu items organized by sections
const menuSections = [
  {
    label: "Application",
    items: [
      {
        title: "Dashboard",
        url: "/dashboard",
        icon: LayoutDashboard,
        isPrimary: true,
      },
    ],
  },
  {
    label: "Services",
    items: [
      {
        title: "Service Catalog",
        url: "/service-catalog",
        icon: LayoutGrid,
      },
      {
        title: "Service Usage",
        url: "/service-usage",
        icon: Activity,
      },
    ],
  },
  {
    label: "Analysis",
    items: [
      {
        title: "Change Impact",
        url: "/change-impact",
        icon: GitCompare,
      },
      {
        title: "What's New",
        url: "/whats-new",
        icon: Megaphone,
      },
      {
        title: "Generate Report",
        url: "/generate-report",
        icon: FileText,
      },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        title: "User Management",
        url: "/user-management",
        icon: UserCog,
        adminOnly: true,
      },
    ],
  },
];

/**
 * Helper function to get user avatar using UI Avatars
 * Creates colorful initials-based avatars
 */
const getUserAvatar = (userFullName, size = 200) => {
  const encodedName = encodeURIComponent(userFullName);
  return `https://ui-avatars.com/api/?name=${encodedName}&size=${size}&background=0854a0&color=fff&bold=true`;
};

export function AppSidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { isCollapsed } = useSidebar();

  const handleLogout = () => {
    logout();
  };

  // Get user info
  const userFullName =
    user?.name ||
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    "User";

  // Generate avatar URL
  const avatarUrl = getUserAvatar(userFullName, 200);

  return (
    <Sidebar
      isCollapsed={isCollapsed}
      className={`bg-white border-r border-gray-200 shadow-lg flex flex-col h-screen flex-shrink-0 ${
        isCollapsed
          ? "w-[80px] min-w-[80px] max-w-[80px]"
          : "w-[280px] min-w-[280px] max-w-[280px]"
      }`}
    >
      <SidebarHeader
        className={`border-b border-gray-100 bg-slate-50 ${
          isCollapsed ? "p-3" : "p-6"
        }`}
      >
        <div
          className={`flex items-center ${
            isCollapsed ? "justify-center" : "justify-center gap-3"
          }`}
        >
          <img
            src={require("../assets/icon/android-chrome-192x192.png")}
            alt="CIA"
            width={isCollapsed ? 32 : 36}
            className="cursor-pointer"
          />
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-bold text-gray-800 leading-tight">
                conRIO
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent
        className={`flex-1 overflow-y-auto ${
          isCollapsed ? "px-2 py-4" : "p-4"
        }`}
      >
        {menuSections.map((section, sectionIndex) => {
          // Filter out admin-only items if user is not admin
          const visibleItems = section.items.filter(
            (item) => !item.adminOnly || user?.role === "admin",
          );

          // Skip section if no visible items
          if (visibleItems.length === 0) return null;

          return (
            <SidebarGroup
              key={section.label}
              className={sectionIndex > 0 ? "mt-6" : ""}
            >
              {!isCollapsed && (
                <SidebarGroupLabel className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 pl-3">
                  {section.label}
                </SidebarGroupLabel>
              )}
              <SidebarMenu
                className={`flex flex-col ${
                  isCollapsed ? "gap-2 items-center" : "gap-1.5"
                }`}
              >
                {visibleItems.map((item) => {
                  const isActive = location.pathname === item.url;
                  const isPrimary = item.isPrimary && isActive;

                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <Link
                          to={item.url}
                          className={cn(
                            "group flex items-center no-underline font-medium transition-all duration-200",
                            isCollapsed
                              ? "w-14 h-14 rounded-xl justify-center"
                              : "w-full px-4 py-3 gap-3 rounded-xl justify-start",
                            isActive
                              ? isPrimary
                                ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                                : "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                              : "text-gray-600 hover:bg-gray-100",
                          )}
                          title={isCollapsed ? item.title : undefined}
                        >
                          <item.icon
                            className={cn(
                              "flex-shrink-0",
                              isCollapsed ? "h-6 w-6" : "h-5 w-5",
                              isActive
                                ? "text-white"
                                : "text-gray-500 group-hover:text-blue-600",
                            )}
                          />
                          {!isCollapsed && (
                            <span className="font-medium leading-5">
                              {item.title}
                            </span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter
        className={`mt-auto border-t border-gray-100 ${
          isCollapsed ? "px-2 py-3" : "p-4"
        }`}
      >
        {/* User Profile Section */}
        <div
          className={`flex items-center bg-gradient-to-br from-slate-50 to-blue-50 border border-blue-100 ${
            isCollapsed
              ? "flex-col gap-2 p-2 rounded-lg"
              : "gap-3 p-4 mb-3 rounded-xl"
          }`}
        >
          {/* User Avatar with UI Avatars */}
          <img
            src={avatarUrl}
            alt={userFullName}
            className={`rounded-full object-cover border-2 border-blue-500 shadow-md ${
              isCollapsed ? "h-8 w-8" : "h-10 w-10"
            }`}
            title={isCollapsed ? userFullName : undefined}
          />
          {!isCollapsed && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 overflow-hidden text-ellipsis whitespace-nowrap">
                  {userFullName}
                </div>
                <div className="text-xs text-gray-600 font-medium overflow-hidden text-ellipsis whitespace-nowrap">
                  {user?.role === "admin" ? "Administrator" : "User"}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white text-gray-600 cursor-pointer transition-all duration-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                title="Logout"
              >
                <LogOut size={14} />
              </button>
            </>
          )}
          {isCollapsed && (
            <button
              onClick={handleLogout}
              className="flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white text-gray-600 cursor-pointer transition-all duration-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
              title="Logout"
            >
              <LogOut size={14} />
            </button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
